import { useState, useMemo } from 'react'
import Card from '../../components/ui/Card'
import { formatCurrency } from '../../lib/format'

function BillingCalendar({ subscriptions }) {
  const [currentDate, setCurrentDate] = useState(new Date())

  const year = currentDate.getFullYear()
  const month = currentDate.getMonth()

  // Get first day of month and total days
  const firstDayOfMonth = new Date(year, month, 1)
  const lastDayOfMonth = new Date(year, month + 1, 0)
  const daysInMonth = lastDayOfMonth.getDate()
  const startingDayOfWeek = firstDayOfMonth.getDay()

  // Group subscriptions by billing date
  const billingsByDate = useMemo(() => {
    const map = {}
    const activeSubscriptions = subscriptions.filter(s => s.is_active && s.next_billing_date)

    activeSubscriptions.forEach(sub => {
      const billingDate = new Date(sub.next_billing_date)
      // Check if billing date is in current month
      if (billingDate.getMonth() === month && billingDate.getFullYear() === year) {
        const day = billingDate.getDate()
        if (!map[day]) map[day] = []
        map[day].push(sub)
      }
    })

    return map
  }, [subscriptions, month, year])

  // Calculate upcoming bills in next 7 days
  const upcomingBills = useMemo(() => {
    const today = new Date()
    const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000)

    return subscriptions
      .filter(s => s.is_active && s.next_billing_date)
      .filter(s => {
        const billingDate = new Date(s.next_billing_date)
        return billingDate >= today && billingDate <= nextWeek
      })
      .sort((a, b) => new Date(a.next_billing_date) - new Date(b.next_billing_date))
  }, [subscriptions])

  const upcomingTotal = upcomingBills.reduce((sum, s) => sum + parseFloat(s.amount), 0)

  const prevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1))
  }

  const nextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1))
  }

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

  const today = new Date()
  const isToday = (day) => {
    return day === today.getDate() &&
           month === today.getMonth() &&
           year === today.getFullYear()
  }

  // Build calendar grid
  const calendarDays = []
  // Empty cells for days before first of month
  for (let i = 0; i < startingDayOfWeek; i++) {
    calendarDays.push(null)
  }
  // Days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day)
  }

  return (
    <div className="billing-calendar-container">
      {/* Upcoming Bills Alert */}
      {upcomingBills.length > 0 && (
        <Card className="mb-4" style={{ background: 'var(--warning-light)', border: '1px solid var(--warning)' }}>
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium" style={{ color: 'var(--warning-dark)' }}>
                Upcoming Bills (Next 7 Days)
              </h4>
              <p className="text-sm" style={{ color: 'var(--warning-dark)' }}>
                {upcomingBills.length} subscription{upcomingBills.length !== 1 ? 's' : ''} billing soon
              </p>
            </div>
            <div className="text-right">
              <span className="text-2xl font-bold" style={{ color: 'var(--warning-dark)' }}>
                {formatCurrency(upcomingTotal)}
              </span>
            </div>
          </div>
          <div className="mt-3">
            {upcomingBills.map(sub => (
              <div key={sub.id} className="flex items-center justify-between py-1 text-sm">
                <span>{sub.name}</span>
                <span>
                  {formatCurrency(sub.amount)} on {new Date(sub.next_billing_date).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Calendar */}
      <Card>
        <div className="calendar-header flex items-center justify-between mb-4">
          <button
            onClick={prevMonth}
            className="btn btn-ghost btn-sm"
          >
            ←
          </button>
          <h3 className="font-medium">
            {monthNames[month]} {year}
          </h3>
          <button
            onClick={nextMonth}
            className="btn btn-ghost btn-sm"
          >
            →
          </button>
        </div>

        <div className="calendar-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: '2px',
        }}>
          {/* Day headers */}
          {dayNames.map(day => (
            <div
              key={day}
              className="text-center text-sm font-medium text-muted py-2"
            >
              {day}
            </div>
          ))}

          {/* Calendar days */}
          {calendarDays.map((day, index) => (
            <div
              key={index}
              style={{
                minHeight: '80px',
                padding: '4px',
                background: day ? (isToday(day) ? 'var(--primary-light)' : 'var(--gray-50)') : 'transparent',
                borderRadius: '4px',
                border: isToday(day) ? '2px solid var(--primary)' : '1px solid var(--gray-200)',
              }}
            >
              {day && (
                <>
                  <div
                    className="text-sm font-medium mb-1"
                    style={{ color: isToday(day) ? 'var(--primary)' : 'var(--gray-600)' }}
                  >
                    {day}
                  </div>
                  {billingsByDate[day] && (
                    <div className="calendar-day-items">
                      {billingsByDate[day].map(sub => (
                        <div
                          key={sub.id}
                          style={{
                            fontSize: '0.625rem',
                            padding: '2px 4px',
                            background: 'var(--error-light)',
                            borderRadius: '2px',
                            marginBottom: '2px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={`${sub.name}: ${formatCurrency(sub.amount)}`}
                        >
                          {sub.name}
                        </div>
                      ))}
                      {billingsByDate[day].length > 0 && (
                        <div
                          style={{
                            fontSize: '0.625rem',
                            fontWeight: 'bold',
                            color: 'var(--error)',
                          }}
                        >
                          {formatCurrency(
                            billingsByDate[day].reduce((sum, s) => sum + parseFloat(s.amount), 0)
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  )
}

export default BillingCalendar
